import React, { useState, useRef, useEffect } from 'react'
import * as THREE from 'three'

export default function VideoPlayer360() {
  const [view, setView] = useState('select') // 'select', 'player', 'settings'
  const [mediaFile, setMediaFile] = useState(null)
  const [mediaType, setMediaType] = useState(null) // 'video' or 'image'
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showChrome, setShowChrome] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Settings with defaults
  const [fov, setFov] = useState(75)
  const [panSensitivity, setPanSensitivity] = useState(1)
  const [rollAngle, setRollAngle] = useState(0)

  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const rendererRef = useRef(null)
  const sceneRef = useRef(null)
  const cameraRef = useRef(null)
  const sphereRef = useRef(null)
  const animationRef = useRef(null)
  const touchStartRef = useRef({ x: 0, y: 0 })
  const rotationRef = useRef({ lon: 0, lat: 0 })
  const seekDuration = useRef(null)

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('service-worker.js')
    }
  }, [])

  const handleFileSelect = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setError('')
    setLoading(true)

    // Check file size (estimate based on available memory)
    const maxSize = 50_000 * 1024 * 1024 // 50GB limit
    if (file.size > maxSize) {
      setError(
        `File too large: ${(file.size / 1024 / 1024).toFixed(
          0
        )}MB. Maximum size is ${maxSize / 1024 / 1024}MB.`
      )
      setLoading(false)
      return
    }

    const url = URL.createObjectURL(file)
    const fileType = file.type.startsWith('video/') ? 'video' : 'image'

    if (fileType === 'video') {
      // Create video element to check aspect ratio
      const video = document.createElement('video')

      video.onloadedmetadata = () => {
        const aspectRatio = video.videoWidth / video.videoHeight
        const expectedRatio = 2.0
        const tolerance = 0.1

        if (Math.abs(aspectRatio - expectedRatio) > tolerance) {
          setError(
            `Invalid aspect ratio: ${aspectRatio.toFixed(
              2
            )}:1. Equirectangular videos must have a 2:1 aspect ratio.`
          )
          URL.revokeObjectURL(url)
          setLoading(false)
          return
        }

        setMediaFile(url)
        setMediaType('video')
        setShowChrome(true)
        setView('player')
        setLoading(false)

        // Reset camera position
        rotationRef.current = { lon: 0, lat: 0 }
      }

      video.onerror = () => {
        setError(
          'Unable to load video file. Please ensure it is a valid video format.'
        )
        URL.revokeObjectURL(url)
        setLoading(false)
      }

      video.src = url
    } else {
      // Create image element to check aspect ratio
      const img = new Image()

      img.onload = () => {
        const aspectRatio = img.width / img.height
        const expectedRatio = 2.0
        const tolerance = 0.1

        if (Math.abs(aspectRatio - expectedRatio) > tolerance) {
          setError(
            `Invalid aspect ratio: ${aspectRatio.toFixed(
              2
            )}:1. Equirectangular images must have a 2:1 aspect ratio.`
          )
          URL.revokeObjectURL(url)
          setLoading(false)
          return
        }

        setMediaFile(url)
        setMediaType('image')
        setShowChrome(false)
        setView('player')
        setLoading(false)

        // Reset camera position
        rotationRef.current = { lon: 0, lat: 0 }
      }

      img.onerror = () => {
        setError(
          'Unable to load image file. Please ensure it is a valid image format.'
        )
        URL.revokeObjectURL(url)
        setLoading(false)
      }

      img.src = url
    }
  }

  const cancelLoading = () => {
    setLoading(false)
    setError('')
  }

  useEffect(() => {
    if (view !== 'player' || !containerRef.current || !mediaFile) return

    // Initialize Three.js scene
    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight

    // Scene
    const scene = new THREE.Scene()
    sceneRef.current = scene

    // Camera
    const camera = new THREE.PerspectiveCamera(fov, width / height, 0.1, 1000)
    camera.position.set(0, 0, 0)
    cameraRef.current = camera

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setSize(width, height)
    container.appendChild(renderer.domElement)
    rendererRef.current = renderer

    let texture

    if (mediaType === 'video') {
      // Video texture
      const video = document.createElement('video')
      video.src = mediaFile
      video.crossOrigin = 'anonymous'
      video.loop = false
      video.playsInline = true
      videoRef.current = video

      video.onloadedmetadata = () => {
        setDuration(video.duration)
      }

      video.ontimeupdate = () => {
        setCurrentTime(video.currentTime)
      }

      texture = new THREE.VideoTexture(video)
      texture.minFilter = THREE.LinearFilter
      texture.magFilter = THREE.LinearFilter
    } else {
      // Image texture
      const loader = new THREE.TextureLoader()
      texture = loader.load(mediaFile)
      texture.minFilter = THREE.LinearFilter
      texture.magFilter = THREE.LinearFilter
    }

    // Sphere geometry (inverted)
    const geometry = new THREE.SphereGeometry(500, 60, 40)
    geometry.scale(-1, 1, 1) // Invert sphere

    const material = new THREE.MeshBasicMaterial({ map: texture })
    const sphere = new THREE.Mesh(geometry, material)
    scene.add(sphere)
    sphereRef.current = sphere

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate)

      // Update camera rotation
      const phi = THREE.MathUtils.degToRad(90 - rotationRef.current.lat)
      const theta = THREE.MathUtils.degToRad(rotationRef.current.lon)

      camera.target = new THREE.Vector3(
        500 * Math.sin(phi) * Math.cos(theta),
        500 * Math.cos(phi),
        500 * Math.sin(phi) * Math.sin(theta)
      )

      camera.lookAt(camera.target)

      // Apply roll
      sphereRef.current.rotation.x = THREE.MathUtils.degToRad(rollAngle)

      renderer.render(scene, camera)
    }

    animate()

    // Handle resize
    const handleResize = () => {
      const w = container.clientWidth
      const h = container.clientHeight
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      cancelAnimationFrame(animationRef.current)
      renderer.dispose()
      geometry.dispose()
      material.dispose()
      texture.dispose()
      container.removeChild(renderer.domElement)
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.src = ''
      }
    }
  }, [view, mediaFile, mediaType, fov, rollAngle])

  useEffect(() => {
    if (view === 'player') {
      if (videoRef.current) {
        videoRef.current.currentTime = seekDuration.current || 0
      }
    }
    if (view !== 'player') {
      setIsPlaying(false)
      if (view !== 'settings') {
        seekDuration.current = 0
      }
    }
  }, [view])

  const handleTouchStart = (e) => {
    if (e.target.closest('.player-chrome') || e.target.closest('button')) return

    const touch = e.touches[0]
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      lon: rotationRef.current.lon,
      lat: rotationRef.current.lat,
    }
  }

  const handleTouchMove = (e) => {
    if (e.target.closest('.player-chrome') || e.target.closest('button')) return

    const touch = e.touches[0]
    const deltaX =
      (touch.clientX - touchStartRef.current.x) * 0.1 * panSensitivity
    const deltaY =
      (touch.clientY - touchStartRef.current.y) * 0.1 * panSensitivity

    rotationRef.current.lon = touchStartRef.current.lon - deltaX
    rotationRef.current.lat = Math.max(
      -85,
      Math.min(85, touchStartRef.current.lat + deltaY)
    )
  }

  const handleMouseDown = (e) => {
    if (e.target.closest('.player-chrome') || e.target.closest('button')) return

    touchStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      lon: rotationRef.current.lon,
      lat: rotationRef.current.lat,
    }

    const handleMouseMove = (e) => {
      const deltaX =
        (e.clientX - touchStartRef.current.x) * 0.1 * panSensitivity
      const deltaY =
        (e.clientY - touchStartRef.current.y) * 0.1 * panSensitivity

      rotationRef.current.lon = touchStartRef.current.lon - deltaX
      rotationRef.current.lat = Math.max(
        -85,
        Math.min(85, touchStartRef.current.lat + deltaY)
      )
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const togglePlayPause = () => {
    if (!videoRef.current) return

    if (isPlaying) {
      videoRef.current.pause()
    } else {
      videoRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const handleSeek = (e) => {
    if (!videoRef.current) return

    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    videoRef.current.currentTime = percent * duration
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleBack = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.src = ''
    }
    if (mediaFile) {
      URL.revokeObjectURL(mediaFile)
    }
    setMediaFile(null)
    setMediaType(null)
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setView('select')
    rotationRef.current = { lon: 0, lat: 0 }
  }

  // View 1: File Selection
  if (view === 'select') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <input
            type="file"
            accept="video/*,image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="video-input"
          />
          <label
            htmlFor="video-input"
            className="inline-block px-8 py-4 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition-colors text-lg font-medium">
            View 360° file
          </label>

          {error && (
            <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg max-w-md mx-auto">
              {error}
            </div>
          )}
        </div>

        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="text-lg">Loading file...</span>
              </div>
              <button
                onClick={cancelLoading}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // View 3: Settings
  if (view === 'settings') {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">Settings</h2>
            <button
              onClick={() => setView('player')}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
              Done
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Field of View: {fov}°
              </label>
              <input
                type="range"
                min="20"
                max="120"
                value={fov}
                onChange={(e) => setFov(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>20°</span>
                <span>120°</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Pan Sensitivity: {panSensitivity.toFixed(2)}x
              </label>
              <input
                type="range"
                min="0.25"
                max="4"
                step="0.25"
                value={panSensitivity}
                onChange={(e) => setPanSensitivity(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0.25x</span>
                <span>4x</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Roll Angle: {rollAngle}°
              </label>
              <input
                type="range"
                min="-180"
                max="180"
                value={rollAngle}
                onChange={(e) => setRollAngle(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>-180°</span>
                <span>180°</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // View 2: Video/Image Player
  return (
    <div
      className="relative w-full h-screen bg-black overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onMouseDown={handleMouseDown}>
      <div ref={containerRef} className="w-full h-full" />

      {/* Chrome Toggle Button - Only for videos */}
      {mediaType === 'video' && (
        <button
          onClick={() => setShowChrome(!showChrome)}
          className="absolute top-4 right-4 w-10 h-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white z-20 hover:bg-opacity-70 transition-all">
          {showChrome ? '−' : '+'}
        </button>
      )}

      {/* Back Button - Always visible for images */}
      {mediaType === 'image' && (
        <button
          onClick={handleBack}
          className="absolute top-4 left-4 w-10 h-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white z-20 hover:bg-opacity-70 transition-all">
          ✕
        </button>
      )}

      {/* Settings Button - Always visible for images */}
      {mediaType === 'image' && (
        <button
          onClick={() => {
            seekDuration.current = videoRef.current?.currentTime || 0
            setView('settings')
          }}
          className="absolute top-4 right-4 w-10 h-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white z-20 hover:bg-opacity-70 transition-all">
          ⚙
        </button>
      )}

      {/* Player Chrome - Only for videos */}
      {mediaType === 'video' && showChrome && (
        <div className="player-chrome absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6 z-10">
          <div className="max-w-4xl mx-auto">
            {/* Timeline */}
            <div
              className="mb-4 h-1 bg-gray-600 rounded-full cursor-pointer relative"
              onClick={handleSeek}>
              <div
                className="absolute top-0 left-0 h-full bg-white rounded-full"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBack}
                  className="w-10 h-10 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full transition-colors">
                  ✕
                </button>

                <button
                  onClick={togglePlayPause}
                  className="w-12 h-12 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full transition-colors">
                  {isPlaying ? '❚❚' : '▶'}
                </button>

                <span className="text-sm">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              <button
                onClick={() => {
                  seekDuration.current = videoRef.current.currentTime
                  setView('settings')
                }}
                className="w-10 h-10 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full transition-colors">
                ⚙
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
